import type { TimePickerTime } from 'customer-flows/components/ui-kit/types'

export default function dynamicPricing({ stores }: CommandArgs) {
  const dynamicPricingStore: DynamicPricingStore = stores['dynamic-pricing']

  /**
   * Enriches a list of TimePickerTimes with price display data.
   *
   * For each time that has no status detail text (sold out, call to book, etc.),
   * looks up the matching AvailabilityTime from `slots` and sets:
   *   - `details` — formatted price string (e.g. "$40")
   *   - `priceGroup` — 'low' (green) for the cheapest slot, 'mid' (grey) otherwise
   *
   * Special case — spots-left cutoff:
   *   When a time has `spotsLeft` set (meaning it would normally show "X Left" in
   *   the detail area) AND pricing is available for that slot, the price takes the
   *   `details` slot and `showCornerFlag` is set to `true` instead. The `spotsLeft`
   *   count is preserved so the parent component can render a legend for the selected
   *   time. If the slot has no pricing data the spots-left detail is left unchanged.
   *
   * Times with other status details (sold out, call to book) are left unchanged.
   * Skipped entirely when `shouldShowPricesForTimes` is false.
   */
  function enrichTimesWithPricing(
    times: TimePickerTime[],
    slots: AvailabilityTime[]
  ): TimePickerTime[] {
    if (!dynamicPricingStore.shouldShowPricesForTimes) return times

    // First pass: resolve price for each priceable time.
    // Times with spots-left details are priceable (pricing overrides them);
    // times with other status details (sold out, call to book) are not.
    const priceMap = new Map<string, { money: Money; formatted: string }>()

    for (const time of times) {
      if (time.details && !time.spotsLeft) continue

      const slot = slots.find((s) => s.from === time.isoTime)
      if (!slot) continue

      const money = dynamicPricingStore.getDisplayPriceForSlot(slot)
      const formatted = money ? dynamicPricingStore.getFormattedPriceForSlot(slot) : null
      if (money && formatted) priceMap.set(time.isoTime, { money, formatted })
    }

    if (priceMap.size === 0) return times

    const lowestAmount = Math.min(
      ...[...priceMap.values()]
        .map(({ money }) => parseFloat(money.amount))
        .filter((n) => !isNaN(n))
    )

    // Second pass: apply price details and group
    return times.map((time) => {
      // Never override sold-out / call-to-book details
      if (time.details && !time.spotsLeft) return time

      const entry = priceMap.get(time.isoTime)
      // No pricing for this slot — keep spots-left detail as-is if present
      if (!entry) return time

      const amount = parseFloat(entry.money.amount)
      const priceGroup = !isNaN(amount) && amount === lowestAmount ? 'low' : 'mid'

      // Spots-left time with pricing: show price in details, move spots info to corner flag
      if (time.spotsLeft) {
        return { ...time, details: entry.formatted, priceGroup, showCornerFlag: true }
      }

      return { ...time, details: entry.formatted, priceGroup }
    })
  }

  return { enrichTimesWithPricing }
}

declare global {
  export type DynamicPricingCommands = ReturnType<typeof dynamicPricing>

  // Add to global scope
  // eslint-disable-next-line no-unused-vars
  interface CommandRegistry {
    'dynamic-pricing': DynamicPricingCommands
  }
}
